import { useEffect, useRef, useState } from "react";
import { Loader2, LocateFixed, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Input } from "./ui/input";
import {
  FACILITY_CONDITIONS,
  FACILITY_KINDS,
  FACILITY_SURFACES,
  facilityConditionLabel,
  facilityKindLabel,
  facilitySurfaceLabel,
} from "../lib/facilities";
import {
  emptyFacilityDraft,
  facilityAddPath,
  facilityCreateBody,
  facilityDraftComplete,
} from "../lib/facilityAdd";
import { draftWithPin, facilityPinWarning, surfacePinFailureMessage } from "../lib/facilityPin";
import {
  GROUND_PIN_FROM_FIELD,
  GROUND_PIN_FROM_MAP,
  groundPin,
  isGroundPin,
  pinFromForm,
  samePin,
} from "../lib/groundEdit";
import { NEAR_ME_BUSY_LABEL, NEAR_ME_LABEL, readDevicePosition } from "../lib/nearMe";
import { SurfacePinMiniMap } from "./SurfacePinMap";

/**
 * Issue #542 — register one surface or one net, from the client.
 *
 * The write path has existed since #165 (`POST /grounds/:groundId/facilities`)
 * and shipped API-only, which left #211/#540's whole rating machinery
 * unreachable on any ground whose surfaces were never seeded through the
 * API — Martin opened Millwater and found no ratings anywhere, because
 * `FacilityList` correctly renders nothing when there are no rows and
 * nothing could create a row. This is the missing gesture.
 *
 * THE RULES ARE `lib/facilityAdd.js` — body, completeness, path, gate —
 * and this component is only the surface, on `ConditionUpdate.jsx`'s
 * pattern exactly: it decides NOTHING about who may press it. The caller
 * gates it with `canAddFacility` (which is `canOfferGroundEdit`, the same
 * predicate the condition update and the correction form mirror), so no
 * control is ever shown that the server would 403. A refusal that still
 * arrives (the server owns the "other players rely on this ground" half no
 * client may be told) is SHOWN, not swallowed.
 *
 * CLOSED UNTIL PRESSED — the `startCorrecting` shape, not a #526
 * SectionCard fold: one button ("Add a surface"), then the form inline in
 * the flow that already holds `GroundEditPanel` and `RatingEditor`.
 *
 * `is_indoor` / `has_lighting` are yes/no CHOICES starting unanswered, not
 * checkboxes: a checkbox's unticked default is exactly the defaulted
 * lighting flag #165's schema comment argues against ("venues that merely
 * never answered"), so Save stays disabled until both are answered.
 * `condition` starts at "Not rated", which IS an answer (#164: an
 * unjustified rating is worse than none). The optional pin travels
 * whole-or-nothing (`ground_facilities_pin_check`).
 *
 * --- ISSUE #548: THE PIN COMES FROM THE MAP, NOT FROM A NUMBER ---
 *
 * #543 shipped the pin as two numeric fields and Martin named the result:
 * finding coordinates is "near impossible", so every surface arrived
 * unpinned — and unpinned is deliberately invisible on the map. Three
 * routes in now, #379's one-position-two-inputs shape:
 *
 *   1. THE MAP. A host that already has one (the ground detail dialog)
 *      passes `pinDraft` — `useSurfacePinDraft()`'s controller — and its
 *      map's taps/drags land here as `pinDraft.pin`. A host with no map
 *      (the admin venue console) passes nothing and this form embeds
 *      `SurfacePinMiniMap`, the same gesture on its own small satellite
 *      map — falling back to the fields alone when the venue itself is
 *      unpinned (no centre, nothing honest to show).
 *   2. "USE MY LOCATION" — the strongest route for the actual case, a
 *      player standing at the ground. The mechanism is `readDevicePosition`
 *      (`lib/nearMe.js`): read ONCE, on a press, never on mount or render;
 *      the position goes into component state and nowhere else until the
 *      user chooses to SAVE it as this surface's pin — the same
 *      second-consent shape as #134's "add this place". Failures get one
 *      plain line each (`surfacePinFailureMessage`), and the form stays
 *      fully usable.
 *   3. THE FIELDS, demoted to a read-back of the gesture but kept: the
 *      keyboard/screen-reader route, and the right one for a surveyed
 *      coordinate. `via` (`lib/groundEdit.js`) says which input moved the
 *      pin last, so a map move fills the fields but a typed value is never
 *      reformatted under the cursor by its own echo.
 *
 * THE DISTANCE SANITY CHECK (`facilityPinWarning`, threshold stated there):
 * a pin far from the venue's own pin draws a warning that names the
 * distance and lets them proceed — the venue pin is explicitly
 * approximate, so far can be true. It never gates Save.
 *
 * `onAdded` receives the SERVER'S row (201 body), never a local guess —
 * the timestamps and id only the server can stamp, and the caller folds
 * the row into the same list every rating and condition control reads
 * from, so the new surface (pin included) arrives on the map with its
 * whole toolbelt and no reload.
 *
 * 44px minimum targets throughout (#134): the person this exists for is
 * standing at a ground holding a phone.
 */

/** One row of exclusive chips — surface, condition, yes/no — RatingEditor's
 * chip look, radio semantics, 44px targets.
 *
 * EXPORTED since #601: the correction form (`FacilityEditForm.jsx`) asks two
 * of the same questions this one does — field-or-net, and surface — and a
 * second copy of the chip row is a second place for the look, the 44px
 * target and the radio semantics to drift (#497's rule: reuse is enforced,
 * copy-paste drifts). It is presentation only; it decides nothing, so
 * sharing it shares no rule. */
export function ChoiceChips({ label, choices, value, onChoose, disabled, testId, hint = null }) {
  return (
    <div className="mb-1.5">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      {hint && <div className="text-[10px] text-slate-400">{hint}</div>}
      <div className="mt-0.5 flex flex-wrap gap-1.5" role="radiogroup" aria-label={label}>
        {choices.map((choice) => {
          const active = value === choice.value;
          return (
            <button
              key={choice.value ?? "unset"}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => onChoose(choice.value)}
              data-testid={`${testId}-${choice.key ?? choice.value}`}
              className={`inline-flex items-center justify-center rounded-md border px-2.5 min-h-11 text-[11px] font-semibold transition disabled:opacity-60 ${active
                ? "border-[#0B2E59] bg-[#0B2E5908] text-[#0B2E59]"
                : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
            >
              {choice.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const YES_NO = [
  { value: true, key: "yes", label: "Yes" },
  { value: false, key: "no", label: "No" },
];

export default function FacilityAddForm({ ground, onAdded, testId, pinDraft = null, autoOpen = null, onFormOpen = null }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(emptyFacilityDraft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // #548's own-map mode: the pin when NO host map is wired. One of the two
  // is always dead — `pinDraft` wins whenever a host provided it.
  const [localPin, setLocalPin] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const path = facilityAddPath(ground?.ground_id);

  const pin = pinDraft ? pinDraft.pin : localPin;
  const setPin = pinDraft ? pinDraft.setPin : setLocalPin;

  /**
   * Issue #547 — arriving OPEN, once, with a pin already placed.
   *
   * The "add a field or net to this ground instead" route out of the
   * duplicate prompt lands here mid-gesture: the person has already chosen
   * to add the surface and already tapped where it is, so making them find
   * the "Add a surface" button and re-tap the map would spend the goodwill
   * the route just earned. `autoOpen` is `{ pin }` from the host, and it
   * fires ONCE per mount (the ref): after the person closes the form, a
   * re-render must not resurrect it. The seed goes through the SAME
   * channel a map tap uses — `pinDraft.open()` then `setPin` (the open
   * activates the session, so the write is not ignored) — and the pin's
   * `map` via lets the existing read-back effect fill the fields.
   */
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!autoOpen || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    setOpen(true);
    // #583 cause 2: an arrival that opens the form pre-armed still arms the map
    // — bring it into view for the same reason a button press does.
    onFormOpen?.();
    if (pinDraft) {
      pinDraft.open();
      if (isGroundPin(autoOpen.pin)) pinDraft.setPin(autoOpen.pin);
    } else if (isGroundPin(autoOpen.pin)) {
      setLocalPin(autoOpen.pin);
    }
  }, [autoOpen, pinDraft, onFormOpen]);

  /** What the fields say RIGHT NOW, readable from the pin effect without
   * depending on them — `GroundEditPanel`'s ref pattern, same reason. */
  const draftRef = useRef(draft);
  draftRef.current = draft;

  /**
   * #548 / #379 — a pin the MAP (or the device) moved, applied to the
   * fields: the read-back. Keyed on the pin alone; the guard is "do the
   * fields already say this?" (`samePin` over `pinFromForm`), and a
   * `field`-via pin is this form's own typing coming back and is applied
   * to nothing — reformatting "-42.4" to "-42.400000" under a cursor is
   * the bug that shape of sync ships with (`GroundEditPanel`'s words).
   */
  useEffect(() => {
    if (!isGroundPin(pin) || pin.via !== GROUND_PIN_FROM_MAP) return;
    if (samePin(pin, pinFromForm(draftRef.current))) return;
    setDraft((d) => draftWithPin(d, pin));
  }, [pin]);

  const set = (field) => (value) => {
    setDraft((d) => ({ ...d, [field]: value }));
    // The other direction: a typed coordinate moves the marker too, or the
    // map and the fields would be two disagreeing answers to one question.
    // A pair that stops being whole takes the marker OFF — half a pin is
    // none (`pinState`), and a marker the fields no longer back is a lie.
    if (field === "latitude" || field === "longitude") {
      const moved = pinFromForm({ ...draftRef.current, [field]: value });
      if (moved && !samePin(moved, pin)) setPin({ ...moved, via: GROUND_PIN_FROM_FIELD });
      if (!moved && isGroundPin(pin)) setPin(null);
    }
  };

  /**
   * #548 route 2. Fires only from the button's click handler — never on
   * mount, never on a render (`readDevicePosition`'s own contract, and the
   * reason it stays the one mechanism: an unasked-for permission prompt is
   * the fastest way to get location denied permanently). The fix is marked
   * `fromDevice` beside its map-via, #134's flag for "a reading of where
   * the PLAYER is, not a spot they clicked".
   */
  const useLocation = async () => {
    if (locating || saving) return;
    setLocating(true);
    setLocationError("");
    const read = await readDevicePosition();
    setLocating(false);
    if (!read.ok) {
      setLocationError(surfacePinFailureMessage(read.reason));
      return;
    }
    setPin({ lat: read.position.lat, lng: read.position.lng, via: GROUND_PIN_FROM_MAP, fromDevice: true });
  };

  /** Warn-not-block: names the distance, never touches Save's predicate. */
  const warning = facilityPinWarning(draft, ground);

  const openForm = () => {
    setOpen(true);
    if (pinDraft) pinDraft.open();
    // #583 cause 2: opening the form arms the host detail map (the pin hint
    // renders ON the map, which can be 280px above the fold). Ask the host to
    // scroll it into view so "the map became an input" is visible from wherever
    // the button was pressed. No-op when there is no host map.
    onFormOpen?.();
  };

  const closeForm = () => {
    setOpen(false);
    setError("");
    setLocationError("");
    setLocalPin(null);
    if (pinDraft) pinDraft.close();
  };

  async function save() {
    const body = facilityCreateBody(draft);
    if (saving || !path || !body) return;
    setSaving(true);
    setError("");
    try {
      const res = await api.post(path, body);
      if (onAdded) onAdded(res.data);
      setDraft(emptyFacilityDraft());
      closeForm();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={openForm}
        data-testid={`${testId}-open`}
        className="mt-2 inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 min-h-11 text-[11px] font-semibold text-slate-600 hover:border-slate-400"
      >
        <Plus size={11} /> Add a surface
      </button>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-slate-200 p-2.5" data-testid={testId}>
      <div className="mb-1.5">
        <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500" htmlFor={`${testId}-name`}>
          Name
        </label>
        <Input
          id={`${testId}-name`}
          value={draft.name}
          maxLength={120}
          disabled={saving}
          onChange={(e) => set("name")(e.target.value)}
          placeholder={'e.g. "Net 1" or "Centre wicket"'}
          className="mt-0.5 h-11"
          data-testid={`${testId}-name`}
        />
      </div>
      {/* #544, `place_type`'s #136 discipline: nothing preselected, Save
          disabled (facilityCreateBody refuses the draft) until answered.
          First question after the name because it is the primary fact —
          what this IS decides which half of "Fields & Nets" it joins. */}
      <ChoiceChips
        label="Field or net?"
        choices={FACILITY_KINDS.map((k) => ({ value: k, label: facilityKindLabel(k) }))}
        value={draft.kind}
        onChoose={set("kind")}
        disabled={saving}
        testId={`${testId}-kind`}
      />
      <ChoiceChips
        label="Surface"
        choices={FACILITY_SURFACES.map((s) => ({ value: s, label: facilitySurfaceLabel(s) }))}
        value={draft.surface}
        onChoose={set("surface")}
        disabled={saving}
        testId={`${testId}-surface`}
      />
      <ChoiceChips
        label="Indoor?"
        choices={YES_NO}
        value={draft.is_indoor}
        onChoose={set("is_indoor")}
        disabled={saving}
        testId={`${testId}-indoor`}
      />
      <ChoiceChips
        label="Floodlights?"
        choices={YES_NO}
        value={draft.has_lighting}
        onChoose={set("has_lighting")}
        disabled={saving}
        testId={`${testId}-lighting`}
      />
      <ChoiceChips
        label="Condition"
        hint="Optional — record what you know, not a guess. Unrated is a state a reader can see."
        choices={[
          { value: null, key: "unrated", label: "Not rated" },
          ...FACILITY_CONDITIONS.map((c) => ({ value: c, label: facilityConditionLabel(c) })),
        ]}
        value={draft.condition}
        onChoose={set("condition")}
        disabled={saving}
        testId={`${testId}-condition`}
      />
      <div className="mb-1.5">
        <div className="text-xs uppercase tracking-wider font-bold text-slate-600">Pin (optional)</div>
        {/* Which routes exist depends on what this instance has: a host map
            above it, its own mini map, or — for an unpinned venue with no
            map centre — the fields and the location button alone. Saying
            the available routes here is what makes the demoted fields read
            as a read-back rather than the ask.

            #583 cause 2: this used to be a 10px text-slate-400 whisper, the
            only on-screen sign that pressing "Add a surface" turned the map
            above into an input (the hint itself renders ON the map, which is
            often scrolled off). At form size next to the fields it demotes,
            the gesture is announced where the eye already is. */}
        <div className="mt-0.5 text-xs text-slate-600">
          {pinDraft
            ? "Tap the map above, drag the pin, or use your location — the numbers fill in for you."
            : isGroundPin(groundPin(ground))
              ? "Tap the map, drag the pin, or use your location — the numbers fill in for you."
              : "Use your location, or type coordinates if you have them."}
        </div>
        {!pinDraft && (
          <SurfacePinMiniMap ground={ground} pin={pin} onMove={setPin} testId={`${testId}-minimap`} />
        )}
        <button
          type="button"
          onClick={useLocation}
          disabled={saving || locating}
          data-testid={`${testId}-use-location`}
          className="mt-1 inline-flex items-center gap-1 rounded-md border border-[#0B2E59] px-2.5 min-h-11 text-[11px] font-semibold text-[#0B2E59] disabled:opacity-60"
        >
          <LocateFixed size={12} className="shrink-0" />
          {locating ? NEAR_ME_BUSY_LABEL : NEAR_ME_LABEL}
        </button>
        {locationError && (
          <p className="mt-1 text-xs text-slate-600" role="status" data-testid={`${testId}-location-error`}>
            {locationError}
          </p>
        )}
        {/* Whole or nothing — the venue pin is approximate, a surface pin is
            authoritative and drives navigation (schema.ts), so half a pair
            keeps Save disabled rather than being sent for the 400. Kept
            beneath the gesture as the read-back, the keyboard route, and
            the paste path for a surveyed coordinate. */}
        <div className="mt-1 grid grid-cols-2 gap-1.5">
          <Input
            value={draft.latitude}
            disabled={saving}
            inputMode="decimal"
            onChange={(e) => set("latitude")(e.target.value)}
            placeholder="Latitude"
            aria-label="Latitude"
            className="h-11"
            data-testid={`${testId}-lat`}
          />
          <Input
            value={draft.longitude}
            disabled={saving}
            inputMode="decimal"
            onChange={(e) => set("longitude")(e.target.value)}
            placeholder="Longitude"
            aria-label="Longitude"
            className="h-11"
            data-testid={`${testId}-lng`}
          />
        </div>
        {warning && (
          <p className="mt-1 text-xs text-amber-700" role="status" data-testid={`${testId}-distance-warning`}>
            {warning.message}
          </p>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || !facilityDraftComplete(draft)}
          data-testid={`${testId}-save`}
          className="inline-flex items-center gap-1 rounded-md bg-[#0B2E59] px-3 min-h-11 text-[11px] font-semibold text-white disabled:opacity-50"
        >
          {saving && <Loader2 size={11} className="animate-spin" />}
          Add surface
        </button>
        <button
          type="button"
          onClick={closeForm}
          disabled={saving}
          data-testid={`${testId}-cancel`}
          className="rounded-md border border-slate-200 px-3 min-h-11 text-[11px] font-semibold text-slate-600"
        >
          Cancel
        </button>
      </div>
      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
